import { 
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachYearOfInterval,
  eachWeekOfInterval,
  startOfWeek,
  endOfWeek,
  isSameDay,
  differenceInDays,
  differenceInCalendarWeeks,
  format
} from 'date-fns';
import { Workstream } from '../types';

export type TimelineView = 'week' | 'month' | 'quarter' | 'year';

export interface TimelineConfig {
  view: TimelineView;
  zoomLevel: number;
  startDate: Date;
  endDate: Date;
}

// Helper to normalize dates to local midnight
const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export const getTimelineDivisions = (config: TimelineConfig) => {
  const { view, startDate, endDate } = config;
  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = normalizeDate(endDate);

  switch (view) {
    case 'week': {
      const weekStart = startOfWeek(normalizedStart, { weekStartsOn: 1 });
      return eachWeekOfInterval(
        { start: weekStart, end: normalizedEnd },
        { weekStartsOn: 1 }
      );
    }
    case 'month':
      return eachMonthOfInterval({ start: normalizedStart, end: normalizedEnd });
    case 'quarter':
      return eachQuarterOfInterval({ start: normalizedStart, end: normalizedEnd });
    case 'year':
      return eachYearOfInterval({ start: normalizedStart, end: normalizedEnd });
  }
};

export const getDivisionWidth = (view: TimelineView, zoomLevel: number): number => {
  const baseWidth = 100;
  switch (view) {
    case 'week':
      return baseWidth * zoomLevel; // Give weeks full width for better visibility
    case 'month':
      return baseWidth * zoomLevel;
    case 'quarter':
      return baseWidth * 3 * zoomLevel;
    case 'year':
      return baseWidth * 12 * zoomLevel;
  }
};

export const formatDivisionLabel = (date: Date, view: TimelineView): string => {
  switch (view) {
    case 'week': {
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      return `${format(date, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
    }
    case 'month':
      return format(date, 'MMM yyyy');
    case 'quarter':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    case 'year':
      return format(date, 'yyyy');
  }
};

export const getWorkstreamPosition = (
  startDate: string | Date,
  endDate: string | Date,
  timelineConfig: TimelineConfig
) => {
  const start = normalizeDate(new Date(startDate));
  const end = normalizeDate(new Date(endDate));
  const configStart = normalizeDate(timelineConfig.startDate);
  const configEnd = normalizeDate(timelineConfig.endDate);
  
  // Clamp dates to timeline range
  const clampedStart = new Date(Math.max(start.getTime(), configStart.getTime()));
  const clampedEnd = new Date(Math.min(end.getTime(), configEnd.getTime()));
  
  const startOffset = differenceInDays(clampedStart, configStart);
  const duration = differenceInDays(clampedEnd, clampedStart);
  const totalDays = differenceInDays(configEnd, configStart);
  
  const divisionWidth = getDivisionWidth(timelineConfig.view, timelineConfig.zoomLevel);
  const totalWidth = divisionWidth * getTimelineDivisions(timelineConfig).length;
  
  const left = Math.max(0, (startOffset / totalDays) * totalWidth);
  const width = Math.max(50, (duration / totalDays) * totalWidth);
  
  // Ensure we don't exceed timeline bounds
  return {
    left: Math.min(left, totalWidth),
    width: Math.min(width, totalWidth - left)
  };
};

export const isCriticalPath = (workstream: Workstream, workstreams: Workstream[]): boolean => {
  if (!workstream.parentId) return false;
  
  const parent = workstreams.find(w => w.id === workstream.parentId);
  if (!parent) return false;
  
  const siblings = workstreams.filter(w => w.parentId === workstream.parentId);
  const workstreamEnd = normalizeDate(new Date(workstream.endDate));
  
  return isSameDay(workstreamEnd, normalizeDate(new Date(parent.endDate))) &&
    siblings.every(sibling => 
      normalizeDate(new Date(sibling.endDate)) <= workstreamEnd || sibling.id === workstream.id
    );
};

export const getTodayPosition = (config: TimelineConfig): number | null => {
  // Normalize all dates to local midnight
  const today = normalizeDate(new Date());
  const startDate = normalizeDate(config.startDate);
  const endDate = normalizeDate(config.endDate);

  // Debug output
  console.log('getTodayPosition calculation:', {
    view: config.view,
    normalizedToday: today.toISOString(),
    normalizedStart: startDate.toISOString(),
    normalizedEnd: endDate.toISOString()
  });

  // If today is outside the timeline range, return null
  if (today < startDate || today > endDate) {
    console.log('Today is outside range');
    return null;
  }

  // Calculate the total width of the timeline
  const divisionWidth = getDivisionWidth(config.view, config.zoomLevel);
  const divisions = getTimelineDivisions(config);
  const totalWidth = divisionWidth * divisions.length;

  if (config.view === 'week') {
    // For week view, calculate position based on calendar weeks
    const weeksElapsed = differenceInCalendarWeeks(
      today,
      startOfWeek(startDate, { weekStartsOn: 1 }),
      { weekStartsOn: 1 }
    );
    const totalWeeks = differenceInCalendarWeeks(
      endDate,
      startOfWeek(startDate, { weekStartsOn: 1 }),
      { weekStartsOn: 1 }
    );
    
    const position = Math.round((weeksElapsed / totalWeeks) * totalWidth);

    console.log('Week view position calculation:', {
      weeksElapsed,
      totalWeeks,
      totalWidth,
      position
    });

    return position;
  }

  // For other views, use day-based calculation
  const daysElapsed = differenceInDays(today, startDate);
  const totalDays = differenceInDays(endDate, startDate);
  const position = Math.round((daysElapsed / totalDays) * totalWidth);

  console.log('Position calculation:', {
    daysElapsed,
    totalDays,
    totalWidth,
    position
  });

  return position;
};